import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {connect} from 'react-redux';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  MarkerType,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {Menu, MenuItem} from '@material-ui/core';
import {
  structuralKey, sortNodesParentFirst, applyHighlight, computeCollapse, getTransitiveMembers,
} from '../util/graphModel';
import {
  deriveDataViewNodes, resolveDisplayTargets, isDerivedId, parseDerivedId,
  derivedTableId, derivedGroupId, isExpandable, isTopLevelPlainApp,
} from '../util/deriveDataView';
import {nodeColor, mergeTheme} from '../util/theme';
import {LineageContext} from './LineageContext';
import AppNode from './AppNode';
import GroupNode from './GroupNode';
import FloatingSystemEdge from './FloatingSystemEdge';
import Palette from './Palette';
import {setRfInstance} from './rfInstance';
import * as A from '../store/lineageActions';

const nodeTypes = {appNode: AppNode, groupNode: GroupNode};
const edgeTypes = {floating: FloatingSystemEdge};

const COLLAPSED_W = 190;
const COLLAPSED_H = 58;

const parseElHandle = (handle) => {
  if (!handle || !handle.startsWith('el-')) return null;
  const body = handle.slice(3);
  const last = body.lastIndexOf('-');
  return last === -1 ? null : body.slice(0, last);
};

const refFor = (displayId, elementId) => {
  const d = parseDerivedId(displayId);
  return d && d.kind === 'table' ?
    {nodeId: d.nodeId, tableId: d.tableId, elementId} :
    {nodeId: displayId, tableId: null, elementId};
};

const buildDisplayEdges = (map, highlight, theme, lineageViewType, derivedNodes) => {
  const anySelection = highlight.nodeIds.length > 0;
  const marker = (on) => ({type: MarkerType.ArrowClosed, width: 16, height: 16, color: on ? theme.edge.highlightColor : theme.edge.color});
  const dirMarkers = (direction, on) => ({
    markerEnd: (direction === 'forward' || direction === 'bidirectional') ? marker(on) : undefined,
    markerStart: (direction === 'backward' || direction === 'bidirectional') ? marker(on) : undefined,
  });

  if (lineageViewType === 'data') {
    const mappingSet = new Set(highlight.mappingIds);
    const nodeById = new Map(map.nodes.map((n) => [n.id, n]));
    const byDisplayId = new Map((derivedNodes || []).map((n) => [n.id, n]));
    const absX = (ownerId, displayId, isCard) => {
      const owner = nodeById.get(ownerId);
      const card = byDisplayId.get(displayId);
      return (owner ? owner.position.x : 0) + (isCard && card ? card.position.x : 0);
    };
    const context = [];
    map.edges.forEach((e) => {
      const srcNode = nodeById.get(e.source);
      const tgtNode = nodeById.get(e.target);
      if (!srcNode || !tgtNode) return;
      resolveDisplayTargets(srcNode).forEach((s) => resolveDisplayTargets(tgtNode).forEach((t) => {
        context.push({
          id: `ctx-${e.id}-${s}-${t}`, source: s, target: t,
          type: 'floating', selectable: false, zIndex: 1001,
          style: {stroke: theme.edge.color, strokeWidth: 1.5, opacity: 0.35, strokeDasharray: '5 4'},
          ...dirMarkers(e.direction, false),
        });
      }));
    });
    const mappings = map.mappings.map((m) => {
      const on = mappingSet.has(m.id);
      const src = m.sourceTableId ? derivedTableId(m.sourceNodeId, m.sourceTableId) : m.sourceNodeId;
      const tgt = m.targetTableId ? derivedTableId(m.targetNodeId, m.targetTableId) : m.targetNodeId;
      const srcRightOfTgt = absX(m.sourceNodeId, src, !!m.sourceTableId) > absX(m.targetNodeId, tgt, !!m.targetTableId);
      const sourceHandle = srcRightOfTgt ? `el-${m.sourceElementId}-source-l` : `el-${m.sourceElementId}-source`;
      const targetHandle = srcRightOfTgt ? `el-${m.targetElementId}-target-r` : `el-${m.targetElementId}-target`;
      return {
        id: `m-${m.id}`, source: src, target: tgt,
        sourceHandle, targetHandle,
        type: 'default', animated: on, zIndex: 1002,
        className: on && m.direction === 'backward' ? 'ln-edge-reverse' : '',
        style: {stroke: on ? theme.edge.highlightColor : theme.edge.color, strokeWidth: on ? theme.edge.highlightWidth : theme.edge.width},
        label: m.label || m.transformation || '',
        data: {mappingId: m.id},
        ...dirMarkers(m.direction, on),
      };
    });
    return [...context, ...mappings];
  }

  const down = new Set(highlight.downstreamEdgeIds);
  const up = new Set(highlight.upstreamEdgeIds);
  const {rep} = computeCollapse(map.nodes);
  return map.edges.map((e) => {
    const source = rep.get(e.source) || e.source;
    const target = rep.get(e.target) || e.target;
    if (source === target) return null;
    const inDown = down.has(e.id);
    const inUp = up.has(e.id);
    const on = inDown || inUp;
    const floating = !!(e.data && e.data.autoDerived);
    return {
      id: e.id, source, target,
      sourceHandle: floating ? undefined : (source === e.source ? (e.sourceHandle || 'node-source') : 'node-source'),
      targetHandle: floating ? undefined : (target === e.target ? (e.targetHandle || 'node-target') : 'node-target'),
      type: floating ? 'floating' : 'default',
      animated: floating ? false : on,
      zIndex: 1001,
      className: on && e.direction === 'backward' ? 'ln-edge-reverse' : '',
      style: {
        stroke: on ? theme.edge.highlightColor : theme.edge.color,
        strokeWidth: on ? theme.edge.highlightWidth : theme.edge.width,
        opacity: anySelection && !on ? 0.15 : 1,
      },
      label: e.label || '',
      data: e.data,
      ...dirMarkers(e.direction, on),
    };
  }).filter(Boolean);
};

const LineageCanvasInner = (props) => {
  const {
    map, viewMode, lineageViewType, highlight, drawerOpen,
    characteristics,
  } = props;
  const rf = useReactFlow();
  const wrapperRef = useRef(null);
  const [menu, setMenu] = useState(null);
  const theme = mergeTheme(props.globalTheme);

  useEffect(() => {
    const id = requestAnimationFrame(() => rf.fitView({padding: 0.2, duration: 300}));
    return () => cancelAnimationFrame(id);
  }, [lineageViewType, rf]);

  const baseNodes = useMemo(
      () => lineageViewType === 'data' ? deriveDataViewNodes(map.nodes, map.mappings) : map.nodes,
      [map.nodes, map.mappings, lineageViewType],
  );
  const orderIds = useMemo(
      () => sortNodesParentFirst(baseNodes).map((n) => n.id),
      [structuralKey(baseNodes)],
  );
  const highlightedNodeSet = useMemo(() => new Set(highlight.nodeIds), [highlight.nodeIds]);
  const highlightedElementSet = useMemo(() => new Set(highlight.elementKeys), [highlight.elementKeys]);
  const displayNodes = useMemo(() => {
    const byId = new Map(baseNodes.map((n) => [n.id, n]));
    const ordered = orderIds.map((id) => byId.get(id)).filter(Boolean);
    let hlSet = highlightedNodeSet;
    if (lineageViewType === 'data' && highlightedNodeSet.size) {
      hlSet = new Set(highlightedNodeSet);
      map.nodes.forEach((n) => {
        if (highlightedNodeSet.has(n.id) && isExpandable(n)) {
          hlSet.add(derivedGroupId(n.id));
          n.data.tables.forEach((t) => hlSet.add(derivedTableId(n.id, t.id)));
        }
      });
    }
    let viewNodes = ordered;
    if (lineageViewType === 'system') {
      const {collapsed, hidden} = computeCollapse(map.nodes);
      if (collapsed.size) {
        viewNodes = ordered.filter((n) => !hidden.has(n.id)).map((n) => collapsed.has(n.id) ? {
          ...n, width: COLLAPSED_W, height: COLLAPSED_H,
          data: {...n.data, __memberCount: getTransitiveMembers(map.nodes, n.id).size},
        } : n);
      }
    }
    return applyHighlight(viewNodes, hlSet, highlightedElementSet);
  }, [baseNodes, orderIds, highlightedNodeSet, highlightedElementSet, lineageViewType, map.nodes]);

  const displayEdges = useMemo(
      () => buildDisplayEdges(map, highlight, theme, lineageViewType, displayNodes),
      [map, highlight, theme, lineageViewType, displayNodes],
  );

  const actions = useMemo(() => {
    const rt = (id) => {
      const d = parseDerivedId(id);
      return d && d.kind === 'table' ? {nodeId: d.nodeId, tableId: d.tableId} : {nodeId: id, tableId: null};
    };
    return {
      selectElement: (id, elId) => {
        const {nodeId, tableId} = rt(id);
        props.selectElement(nodeId, elId, tableId);
      },
      addElement: (id, el) => {
        const {nodeId, tableId} = rt(id);
        props.addElement(nodeId, el, tableId);
      },
      updateElement: (id, elId, upd) => {
        const {nodeId, tableId} = rt(id);
        props.updateElement(nodeId, elId, upd, tableId);
      },
      removeElement: (id, elId) => {
        const {nodeId, tableId} = rt(id);
        props.removeElement(nodeId, elId, tableId);
      },
      openNodeDrawer: props.openDrawer,
      toggleGroupCollapse: props.toggleCollapse,
      updateNode: props.updateNode,
      fetchDdColumns: props.fetchDdColumns,
      // Rename a card's title inline: a derived child card renames its table (name); a flat
      // node card renames the node's label.
      renameCard: (id, name) => {
        const {nodeId, tableId} = rt(id);
        if (tableId) props.updateTable(nodeId, tableId, {name});
        else props.updateNode(nodeId, {data: {label: name}});
      },
    };
  }, [props.selectElement, props.addElement, props.updateElement, props.removeElement,
    props.openDrawer, props.toggleCollapse, props.updateNode, props.fetchDdColumns, props.updateTable]);

  const contextValue = useMemo(
      () => ({lineageViewType, viewMode, theme, actions, characteristics, ddColumns: props.ddColumns}),
      [lineageViewType, viewMode, theme, actions, characteristics, props.ddColumns],
  );

  const collapsedIds = useMemo(
      () => new Set(map.nodes.filter((n) => n.kind === 'group' && n.data && n.data.collapsed).map((n) => n.id)),
      [map.nodes],
  );

  // In data view a top-level plain app node's position is display-only (dvNodePos), like a derived
  // card (dataPos) / cluster (dvGroupPos) — it must never round-trip into map.nodes, which holds the
  // system-view position. shared predicate with deriveDataView so the freeze + routing agree.
  const plainNodeIds = useMemo(
      () => lineageViewType === 'data' ?
        new Set(map.nodes.filter(isTopLevelPlainApp).map((n) => n.id)) : null,
      [lineageViewType, map.nodes],
  );
  const onNodesChange = useCallback(
      (changes) => {
        changes.forEach((c) => {
          if (c.type !== 'position' || !c.position) return;
          if (isDerivedId(c.id)) {
            const d = parseDerivedId(c.id);
            if (!d) return;
            if (d.kind === 'table') props.updateTablePos(d.nodeId, d.tableId, c.position);
            else if (d.kind === 'group') props.updateGroupPos(d.nodeId, c.position);
          } else if (plainNodeIds && plainNodeIds.has(c.id)) {
            props.updateDataNodePos(c.id, c.position);
          }
        });
        props.applyNodeChanges(
            changes.filter((c) => {
              if (isDerivedId(c.id)) return false;
              if (c.type === 'dimensions' && collapsedIds.has(c.id)) return false;
              // Filter EVERY position change for a routed plain node (incl. the drag-end
              // {dragging:false} carrying no position) so none reach map.nodes.
              if (plainNodeIds && c.type === 'position' && plainNodeIds.has(c.id)) return false;
              return true;
            }),
        );
      },
      [props, collapsedIds, plainNodeIds],
  );
  const onEdgesChange = useCallback((changes) => props.applyEdgeChanges(changes), [props]);
  const onNodeDragStop = useCallback(() => props.commitHistory(), [props]);

  const connectStartType = useRef('source');
  const onConnectStart = useCallback((e, params) => {
    connectStartType.current = (params && params.handleType) || 'source';
  }, []);
  const onConnect = useCallback((params) => {
    const direction = connectStartType.current === 'target' ? 'backward' : 'forward';
    connectStartType.current = 'source';
    const srcEl = parseElHandle(params.sourceHandle);
    const tgtEl = parseElHandle(params.targetHandle);
    if (lineageViewType === 'data' && srcEl && tgtEl) {
      props.connectElements(refFor(params.source, srcEl), refFor(params.target, tgtEl), direction);
    } else {
      props.connectNodes(params.source, params.target, params.sourceHandle, params.targetHandle, direction);
    }
  }, [lineageViewType, props]);

  const onNodeClick = useCallback((e, node) => {
    const d = parseDerivedId(node.id);
    if (d) {
      props.selectNode(d.nodeId);
      // Clicking a child/table card opens the inspector scoped to that child (activeTableId).
      if (d.kind === 'table' && viewMode === 'edit') props.openDrawer('edit', d.nodeId, null, d.tableId);
      return;
    }
    props.selectNode(node.id);
    if (viewMode === 'edit') props.openDrawer('edit', node.id);
  }, [viewMode, props]);

  const onPaneClick = useCallback(() => {
    props.clearSelection();
    if (drawerOpen) props.closeDrawer();
    setMenu(null);
  }, [drawerOpen, props]);

  const onNodeContextMenu = useCallback((e, node) => {
    e.preventDefault();
    setMenu({kind: 'node', x: e.clientX, y: e.clientY, target: node});
  }, []);
  const onEdgeContextMenu = useCallback((e, edge) => {
    e.preventDefault();
    if (edge.id.startsWith('ctx-')) return;
    const kind = edge.id.startsWith('m-') ? 'mapping' : 'edge';
    setMenu({kind, x: e.clientX, y: e.clientY, target: edge});
  }, []);
  const onPaneContextMenu = useCallback((e) => {
    e.preventDefault();
    if (viewMode !== 'edit') return;
    const pos = rf.screenToFlowPosition({x: e.clientX, y: e.clientY});
    setMenu({kind: 'pane', x: e.clientX, y: e.clientY, target: pos});
  }, [rf, viewMode]);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);
  const onDrop = useCallback((e) => {
    e.preventDefault();
    const appType = e.dataTransfer.getData('application/lineage-appType');
    if (!appType) return;
    const position = rf.screenToFlowPosition({x: e.clientX, y: e.clientY});
    props.createNode(appType, position);
  }, [rf, props]);

  const closeMenu = () => setMenu(null);

  return (
    <LineageContext.Provider value={contextValue}>
      <ReactFlow
        ref={wrapperRef}
        nodes={displayNodes}
        edges={displayEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeDragStop={onNodeDragStop}
        onConnect={onConnect}
        onConnectStart={onConnectStart}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        onNodeContextMenu={onNodeContextMenu}
        onEdgeContextMenu={onEdgeContextMenu}
        onPaneContextMenu={onPaneContextMenu}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onInit={setRfInstance}
        deleteKeyCode={viewMode === 'edit' ? ['Backspace', 'Delete'] : []}
        nodesDraggable={viewMode === 'edit'}
        nodesConnectable={viewMode === 'edit'}
        elementsSelectable
        minZoom={0.1}
        maxZoom={2}
        fitView
        proOptions={{hideAttribution: true}}
      >
        <Background color="#dfe3e8" gap={18} />
        <Controls />
        <MiniMap
          nodeColor={(n) => n.type === 'groupNode' ? theme.group : nodeColor(theme, n.data && n.data.appType)}
          maskColor="rgba(0,0,0,0.05)"
        />
      </ReactFlow>

      {viewMode === 'edit' && map.nodes.length === 0 && (
        <div className="ln-empty" data-testid="lineage-empty">
          <i className="material-icons">account_tree</i>
          <div className="ln-empty-title">No nodes yet</div>
          <div className="ln-empty-sub">Drag a type from the palette, or right-click the canvas to add a node.</div>
        </div>
      )}

      {viewMode === 'edit' && <Palette />}

      {menu && (
        <Menu
          open
          onClose={closeMenu}
          anchorReference="anchorPosition"
          anchorPosition={{top: menu.y, left: menu.x}}
        >
          {menu.kind === 'node' && [
            <MenuItem key="hl" onClick={() => {
              props.selectNode(menu.target.id);
              closeMenu();
            }}>Highlight up/downstream</MenuItem>,
            menu.target.type === 'groupNode' ?
              <MenuItem key="ug" onClick={() => {
                props.ungroup(menu.target.id);
                closeMenu();
              }}>Ungroup</MenuItem> : null,
            <MenuItem key="del" onClick={() => {
              props.removeNode(menu.target.id);
              closeMenu();
            }}>Delete</MenuItem>,
          ]}
          {menu.kind === 'edge' && [
            <MenuItem key="dir" onClick={() => {
              props.cycleEdgeDirection(menu.target.id);
              closeMenu();
            }}>Cycle direction (fwd/back/bi)</MenuItem>,
            <MenuItem key="del" onClick={() => {
              props.removeEdge(menu.target.id);
              closeMenu();
            }}>Delete edge</MenuItem>,
          ]}
          {menu.kind === 'mapping' && [
            <MenuItem key="dir" onClick={() => {
              props.cycleMappingDirection(menu.target.data.mappingId);
              closeMenu();
            }}>Cycle direction (fwd/back/bi)</MenuItem>,
            <MenuItem key="del" onClick={() => {
              props.removeMapping(menu.target.data.mappingId);
              closeMenu();
            }}>Delete connection</MenuItem>,
          ]}
          {menu.kind === 'pane' && ['application', 'database', 'service', 'api', 'file'].map((t) => (
            <MenuItem key={t} onClick={() => {
              props.createNode(t, menu.target);
              closeMenu();
            }}>Add {t}</MenuItem>
          ))}
        </Menu>
      )}
    </LineageContext.Provider>
  );
};

const mapStateToProps = (state) => ({
  map: state.lineageReducer.map,
  viewMode: state.lineageReducer.viewMode,
  lineageViewType: state.lineageReducer.lineageViewType,
  highlight: state.lineageReducer.highlight,
  drawerOpen: state.lineageReducer.drawerOpen,
  characteristics: state.lineageReducer.characteristics,
  ddColumns: state.lineageReducer.ddColumns,
  globalTheme: state.lineageReducer.globalTheme,
});

const mapDispatchToProps = {
  applyNodeChanges: A.applyNodeChanges,
  applyEdgeChanges: A.applyEdgeChanges,
  commitHistory: A.commitHistory,
  connectNodes: A.connectNodes,
  connectElements: A.connectElements,
  createNode: A.createNode,
  updateNode: A.updateNode,
  selectNode: A.selectNode,
  selectElement: A.selectElement,
  clearSelection: A.clearSelection,
  addElement: A.addElement,
  updateElement: A.updateElement,
  removeElement: A.removeElement,
  updateTable: A.updateTable,
  updateTablePos: A.updateTablePos,
  updateGroupPos: A.updateGroupPos,
  updateDataNodePos: A.updateDataNodePos,
  removeNode: A.removeNode,
  removeEdge: A.removeEdge,
  removeMapping: A.removeMapping,
  cycleEdgeDirection: A.cycleEdgeDirection,
  cycleMappingDirection: A.cycleMappingDirection,
  ungroup: A.ungroup,
  toggleCollapse: A.toggleCollapse,
  openDrawer: A.openDrawer,
  closeDrawer: A.closeDrawer,
  fetchDdColumns: A.fetchDdColumns,
};

const ConnectedCanvas = connect(mapStateToProps, mapDispatchToProps)(LineageCanvasInner);

const LineageCanvas = () => (
  <ReactFlowProvider>
    <ConnectedCanvas />
  </ReactFlowProvider>
);

export default LineageCanvas;
